import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates if a redirect URL is safe to use
 * Prevents open redirect vulnerabilities by:
 * - Allowing relative paths starting with '/' (but not '//')
 * - Allowing same-origin absolute URLs (localhost in development)
 * - Rejecting protocol-relative URLs (//evil.com)
 * - Rejecting external URLs
 * - Rejecting dangerous protocols (javascript:, data:, etc.)
 */
export function isSafeRedirectUrl(url: string | null | undefined): boolean {
  // Reject null, undefined, or empty strings
  if (!url || typeof url !== "string" || url.trim() === "") {
    return false;
  }

  const trimmedUrl = url.trim();

  // Reject protocol-relative URLs (//evil.com)
  if (trimmedUrl.startsWith("//")) {
    return false;
  }

  // Reject backslash tricks (\\evil.com or \/\/evil.com)
  if (trimmedUrl.includes("\\")) {
    return false;
  }

  // Reject dangerous protocols
  const dangerousProtocols = [
    "javascript:",
    "data:",
    "vbscript:",
    "file:",
    "about:",
  ];
  const lowerUrl = trimmedUrl.toLowerCase();
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return false;
    }
  }

  // Allow relative paths starting with '/' (but not '//')
  if (trimmedUrl.startsWith("/")) {
    return true;
  }

  // For absolute URLs, parse and validate
  try {
    const parsedUrl = new URL(trimmedUrl);

    // Only allow http/https protocols
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return false;
    }

    // Allow localhost in all forms
    const hostname = parsedUrl.hostname.toLowerCase();
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname.endsWith(".localhost")
    ) {
      return true;
    }

    // Reject all other absolute URLs (external domains)
    return false;
  } catch {
    // If URL parsing fails, it's not a valid absolute URL
    // and didn't match our relative path check, so reject it
    return false;
  }
}
